// 상단 고정줄 아래층의 값, 열람과 구성 두 화면이 함께 쓰는 계산
//
// ── 왜 한 파일인가 ──────────────────────────────────────────────────────────
//
// 같은 방의 CT 와 연맹원이 같은 순간에 고정줄을 보면 같은 T− 와 같은 요약을 읽어야 한다. 예전에는
// 열람 attack 화면만 이 값을 셌고 구성 화면은 빈 값을 상수로 넘겼다(D4 기준 판의 중대 새 발견,
// confirm 뒤에도 구성 화면 고정줄이 「T− --:-- ops 없음」). 두 화면이 각자 세면 언젠가 하나가 다른
// 규칙으로 갈리므로, 계산은 여기 하나에 두고 두 화면은 결과를 그리기만 한다.
//
// ── T− 가 가리키는 것 ──────────────────────────────────────────────────────
//
// **아직 열지 않은 줄 가운데 가장 이른 개시까지다** (2026-10-13 사용자 결정). 고른 rally 와 무관하다.
// 개시 시각이 지금과 같거나 지났으면 연 줄로 친다. 모든 줄이 개시를 지나면 T− 칸에는 숫자 대신
// 「개시 끝」 상태 글자가 선다. 착탄까지 세지 않는 이유는 같은 칸이 개시와 착탄 두 뜻을 갖게 되기
// 때문이다(카드의 「착탄은 카운트하지 않는다」 결정과 같은 결).
//
// ── 두 화면이 다른 것은 하나뿐이다 ──────────────────────────────────────────
//
// confirm(내부 이름 dispatch) 전에는 구성 화면만 줄 개수를 보이고 열람은 「ops 없음」을 유지한다
// (사용자 답, 2026-10-13). CT 에게는 조립 중인 줄이 있으니 「없음」이 거짓이고, 연맹원에게는 고를
// 대상이 아직 없으니 「없음」이 행동과 맞는다. dispatch 뒤의 계산과 글자는 두 화면이 같다.
//
// 이 파일은 시계를 읽지 않는다. 지금 시각은 부르는 쪽이 넘긴다. 같은 화면의 카드, 신호 존, 고정줄이
// 한 `nowMs` 를 써야 서로 다른 순간을 가리키지 않는다.

import { duration } from '../../core/time/brand.ts'
import { formatSignedOffset } from '../../core/timing/format.ts'
import { defenseBand, type EnemyLine } from '../defense/derive.ts'
import type { ShellStrings } from '../../i18n/index.ts'
import { formatRemaining } from '../screens/view-attack/live-clock.ts'

/** 열람 행(`ViewRow`)에서 고정줄이 읽는 부분. 열람 행이 그대로 들어온다. */
export interface BandRow {
  rosterName: string
  openMs: number
  rally: { offsetMs: number }
}

export interface AttackBandInput {
  /** 시퀀스 상태. 시퀀스가 없으면 null. */
  state: 'draft' | 'confirmed' | 'dispatched' | null
  /** 시퀀스의 줄 수. 개시 시각을 못 풀어도 줄 수는 안다. */
  rallyCount: number
  /** dispatch 뒤 개시 시각을 푼 행. dispatch 전에는 빈 배열이다. */
  rows: readonly BandRow[]
}

/**
 * 아래층 attack 의 상태 다섯.
 *
 * - `none`: 시퀀스가 없거나 줄이 0. 두 화면 모두 「ops 없음」.
 * - `before-dispatch`: 조립 중이거나 조립을 마쳤고 아직 confirm 전. 구성 화면만 개수를 보인다.
 * - `next`: confirm 뒤, 아직 열지 않은 줄이 있다. 그 가운데 가장 이른 개시까지 센다.
 * - `all-open`: confirm 뒤, 모든 줄이 개시를 지났다.
 * - `unsolved`: confirm 뒤인데 개시 시각을 풀지 못했다(서버가 두 겹으로 막는 상태라 정상 경로에서는 없다).
 *   개수만 말하고 T− 는 비운다. 「개시 끝」이라고 말하면 거짓이다.
 */
export type AttackBand =
  | { kind: 'none' }
  | { kind: 'before-dispatch'; count: number }
  | { kind: 'next'; count: number; remainingMs: number; rosterName: string; offsetMs: number }
  | { kind: 'all-open'; count: number }
  | { kind: 'unsolved'; count: number }

export function attackBand(input: AttackBandInput, nowMs: number): AttackBand {
  if (input.state === null || input.rallyCount === 0) return { kind: 'none' }
  const count = input.rallyCount
  if (input.state !== 'dispatched') return { kind: 'before-dispatch', count }
  if (input.rows.length === 0) return { kind: 'unsolved', count }

  // 같은 개시 시각이 둘이면 시퀀스 순서의 앞 줄을 고른다. 정렬이 안정적이라 원래 순서가 남는다.
  const upcoming = input.rows
    .filter((row) => row.openMs > nowMs)
    .sort((a, b) => a.openMs - b.openMs)[0]
  if (upcoming === undefined) return { kind: 'all-open', count }
  return {
    kind: 'next',
    count,
    remainingMs: upcoming.openMs - nowMs,
    rosterName: upcoming.rosterName,
    offsetMs: upcoming.rally.offsetMs,
  }
}

/**
 * 남은 시간 칸의 상태 셋. 고정줄이 칸의 잉크를 이 값으로 고른다.
 *
 * - `idle`: 셀 것이 없다. 칸에는 자리 고정 `T− --:--` 가 선다.
 * - `live`: 세는 중이다. 칸에는 T− 숫자가 선다.
 * - `done`: 모든 개시가 지났다. 칸에는 「개시 끝」 상태 글자가 선다.
 *
 * 화면이 글자를 비교해 상태를 되짚지 않게 계산이 상태를 함께 넘긴다. 글자로 고르면 고정줄과 계산이 다른 언어 묶음을 읽는 날 조용히
 * 틀린다(앞 회차 리뷰의 지적, 2026-10-13).
 */
export type RemainingState = 'idle' | 'live' | 'done'

/**
 * 「다음」 요약을 네 조각으로 나눈 것. 네 조각을 차례로 이으면 `summary` 와 글자 하나까지 같다.
 *
 * ── 왜 나누는가 ──────────────────────────────────────────────────────────────
 *
 * 요약이 한 문자열이면 폭이 모자랄 때 끝부터 잘린다. 끝에 있는 것이 간격 숫자라, 340 폭에서 「rally 3 줄, 다음 홍길동 +0:0…」처럼
 * CT 와 연맹원이 읽어야 할 숫자가 먼저 사라졌다(D4 「후」 판 1 의 중대 새 발견 D4F-1-01). 2026-10-15 사용자 결정(「수와 이름을 지킴」)은
 * 글자와 형식을 그대로 두고, 폭이 모자라면 줄 수 조각이 먼저 말줄임으로 줄고 이름은 그다음이며 간격 숫자는 항상 보이게 하는 것이다.
 * 고정줄이 조각마다 줄어드는 순서를 다르게 주려면 조각의 경계를 알아야 하므로 계산이 함께 넘긴다.
 *
 * 조각의 경계는 언어 묶음 문장의 자리표시자 위치로 가른다(세션 선택). 언어마다 낱말 순서가 다를 수 있어, 글자를 화면에서 다시
 * 짜 맞추지 않고 문장이 정한 순서를 그대로 쓴다.
 */
export interface SummaryParts {
  /** 줄 수 조각(「rally 3 줄,」, 영어 「Rallies: 3,」). 폭이 모자라면 가장 먼저 말줄임으로 준다. */
  count: string
  /** 이름 앞 낱말과 그 앞뒤 빈칸(「 다음 」, 영어 「 next 」). 줄지 않는다. */
  lead: string
  /** 다음 줄의 roster 이름. 줄 수 조각이 다 준 뒤에도 모자라면 말줄임으로 준다. */
  roster: string
  /** 이름 뒤부터 끝까지(「 +0:00.5」). 간격 숫자가 여기 있고 줄지 않는다. */
  tail: string
}

/** 고정줄 아래층이 받는 값. `TopBarLower` 의 ops 갈래와 같은 모양이다. */
export interface BandText {
  remaining: string | null
  remainingState: RemainingState
  summary: string
  /** 「다음」 요약일 때만 있다. 없으면 고정줄은 요약을 한 덩어리로 그리고 끝을 자른다(줄 수만, ops 없음, defense). */
  summaryParts?: SummaryParts
}

/** 「다음」 문장을 조각 틀 셋으로 가른 것. 아직 자리표시자가 남아 있다. */
interface NextTemplate {
  count: string
  lead: string
  tail: string
}

const ROSTER_SLOT = '{roster}'
const OFFSET_SLOT = '{offset}'

/**
 * 「다음」 문장(`bandAttackNext`)을 자리표시자 위치로 가른다.
 *
 * - 이름 자리(`{roster}`) 뒤부터 끝까지가 꼬리다. 간격 숫자 자리(`{offset}`)가 그 안에 있어야 한다.
 * - 이름 자리 앞은 마지막 낱말(「다음」, 「next」)과 그 앞 빈칸을 이름 쪽에 붙이고, 그 앞 전부를 줄 수 조각으로 둔다.
 *
 * 이 모양이 아닌 문장(이름 자리가 없거나 간격 숫자가 이름보다 앞)이면 null 이다. 그때 고정줄은 요약을 한 덩어리로 그린다.
 * 지금 두 언어 문장은 이 모양이고, 단위 검사가 그것을 지킨다.
 */
export function splitNextTemplate(template: string): NextTemplate | null {
  const rosterAt = template.indexOf(ROSTER_SLOT)
  if (rosterAt < 0) return null
  const tail = template.slice(rosterAt + ROSTER_SLOT.length)
  if (!tail.includes(OFFSET_SLOT)) return null
  const head = template.slice(0, rosterAt)
  // 이름 앞 마지막 낱말의 앞 빈칸 자리. 빈칸이 없으면(낱말 하나뿐) 줄 수 조각은 비고 머리 전부가 이름 쪽에 붙는다.
  const cut = head.trimEnd().search(/\s\S*$/)
  if (cut < 0) return { count: '', lead: head, tail }
  return { count: head.slice(0, cut), lead: head.slice(cut), tail }
}

/**
 * 문장의 자리표시자를 채운다. 이름은 맨 뒤에, 함수로 넣는다.
 *
 * 문자열로 넣으면 `String.replaceAll` 이 이름 안의 `$&`, `$'` 같은 글자를 바꿔 넣기 패턴으로 읽는다(이 저장소가 이미 한 번 만난 함정).
 * 이름을 간격 숫자보다 먼저 넣으면 이름 안의 「{offset}」 이 간격 숫자로 바뀐다. 그래서 이름은 마지막에, 함수로 넣는다. 줄 수와 간격 숫자는
 * 우리가 만든 숫자 글자라 그대로 넣어도 된다.
 */
function fillSlots(piece: string, count: number, rosterName: string, offset: string): string {
  return piece
    .replaceAll('{n}', String(count))
    .replaceAll(OFFSET_SLOT, offset)
    .replaceAll(ROSTER_SLOT, () => rosterName)
}

/** 「다음」 요약의 조각 넷. 이름은 문장에 넣지 않고 따로 둔다(이름 안의 글자가 자리표시자로 읽히지 않게). */
function nextParts(t: ShellStrings, count: number, rosterName: string, offset: string): SummaryParts | null {
  const template = splitNextTemplate(t.bandAttackNext)
  if (template === null) return null
  const fill = (piece: string): string => fillSlots(piece, count, rosterName, offset)
  return { count: fill(template.count), lead: fill(template.lead), roster: rosterName, tail: fill(template.tail) }
}

/**
 * 상태를 글자로 옮긴다. `audience` 가 갈리는 자리는 confirm 전 하나뿐이다.
 *
 * 요약의 「다음」 부분은 상대 offset 만 싣는다. 그 자리는 delta 최소 요약이라 절대 시각을 넣지 않는다.
 */
export function attackBandText(
  band: AttackBand,
  t: ShellStrings,
  audience: 'view' | 'compose',
): BandText {
  const count = (n: number): string => t.bandAttackCount.replaceAll('{n}', String(n))
  switch (band.kind) {
    case 'none':
      return { remaining: null, remainingState: 'idle', summary: '' }
    case 'before-dispatch':
      return { remaining: null, remainingState: 'idle', summary: audience === 'compose' ? count(band.count) : '' }
    case 'unsolved':
      return { remaining: null, remainingState: 'idle', summary: count(band.count) }
    case 'all-open':
      return { remaining: t.remainingAllOpen, remainingState: 'done', summary: count(band.count) }
    case 'next': {
      const offset = formatSignedOffset(duration(band.offsetMs))
      const parts = nextParts(t, band.count, band.rosterName, offset)
      const live = { remaining: formatRemaining(band.remainingMs), remainingState: 'live' as const }
      if (parts === null) {
        return { ...live, summary: fillSlots(t.bandAttackNext, band.count, band.rosterName, offset) }
      }
      // 요약 글자는 조각을 이은 것이다. 두 값이 따로 만들어지면 언젠가 고정줄이 그린 글자와 요약이 갈린다.
      return { ...live, summary: parts.count + parts.lead + parts.roster + parts.tail, summaryParts: parts }
    }
  }
}

/**
 * defense 아래층. 계산은 `defenseBand`(다음 적 착탄까지, 미상 포함 개수)를 그대로 쓰고 글자만 여기서 붙인다.
 * defense 에는 dispatch 가 없어 confirm 전과 뒤의 구분이 없다.
 */
export function defenseBandText(
  lines: readonly EnemyLine[],
  nowMs: number,
  t: ShellStrings,
): BandText {
  const summary = defenseBand(lines, nowMs)
  return {
    remaining: summary.nextImpactInMs === null ? null : formatRemaining(summary.nextImpactInMs),
    remainingState: summary.nextImpactInMs === null ? 'idle' : 'live',
    summary: t.bandDefenseCount.replaceAll('{n}', String(summary.count)),
  }
}
